<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import BcsDropdown from '../BcsDropdown.vue'
import { ContentLibraryTemplateService } from '../../services/ContentLibraryTemplateService'
import { ContentLibraryService } from '../../services/ContentLibraryService'
import { handleErrors } from '../../utils/errorHandler'
import { formatAgencyDropdownLabel } from '../../utils/agencyLabelHelpers'
import { AGENCY_PRERENDER_DISPLAY_STATUS } from './constants'
import {
  formatPrerenderTimestamp,
  summarizeFormats,
  matchesAgencyPrerenderSearch,
  pickAgencyJob,
  deriveAgencyPrerenderDisplayStatus,
  isActivePrerenderJobStatus
} from './helpers'

const props = defineProps({
  active: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['notify'])

const contentLibraryTemplateService = ContentLibraryTemplateService()
const contentLibraryService = ContentLibraryService()

const loadingAgencies = ref(false)
const loadingRows = ref(false)
const errorMessage = ref('')
const agencyOptions = ref([])
const selectedAgencyId = ref('')
const agencyMeta = ref(null)
const agencyRows = ref([])
const searchQuery = ref('')
const regeneratingItemId = ref(null)

const previousJobStatusByItem = new Map()
let unsubscribePrerenderStatus = null
let silentRefreshTimer = null

const agencyDropdownOptions = computed(() =>
  agencyOptions.value.map((agency) => ({
    value: agency.id,
    text: formatAgencyDropdownLabel(agency)
  }))
)

const selectedAgencyName = computed(() => {
  const match = agencyOptions.value.find((agency) => agency.id === selectedAgencyId.value)
  return match?.name || ''
})

const filteredRows = computed(() => {
  const term = String(searchQuery.value || '').trim().toLowerCase()
  if (!term) return agencyRows.value
  return agencyRows.value.filter((row) => matchesAgencyPrerenderSearch(row, term))
})

const statusSummary = computed(() => {
  const counts = {}
  agencyRows.value.forEach((row) => {
    const key = row.displayStatus?.key || 'missing'
    counts[key] = (counts[key] || 0) + 1
  })
  return counts
})

function statusMeta(row) {
  const key = row?.displayStatus?.key || 'missing'
  return AGENCY_PRERENDER_DISPLAY_STATUS[key] || AGENCY_PRERENDER_DISPLAY_STATUS.missing
}

function isRowProcessing(row) {
  return row?.displayStatus?.key === 'processing'
}

function syncJobStatusTracker(rows) {
  previousJobStatusByItem.clear()
  rows.forEach((row) => {
    previousJobStatusByItem.set(row.itemId, row.agencyJobStatus || null)
  })
}

function scheduleSilentRefresh() {
  if (silentRefreshTimer) {
    clearTimeout(silentRefreshTimer)
  }
  silentRefreshTimer = setTimeout(() => {
    silentRefreshTimer = null
    if (props.active && selectedAgencyId.value) {
      loadAgencyPrerenderRows({ silent: true })
    }
  }, 1500)
}

function applyRowFromRealtime(row, freshItem) {
  const jobs = freshItem.CACHE_JOBS || {}
  const agencyJob = pickAgencyJob(jobs, selectedAgencyId.value)
  const prevStatus = previousJobStatusByItem.get(row.itemId)
  const nextStatus = agencyJob?.status || null

  if (prevStatus && isActivePrerenderJobStatus(prevStatus) && nextStatus === 'processed') {
    scheduleSilentRefresh()
  }
  previousJobStatusByItem.set(row.itemId, nextStatus)

  const whitelabelEligible = agencyMeta.value?.whitelabelEligible ?? false
  const displayStatus = deriveAgencyPrerenderDisplayStatus({
    agencyJob,
    formats: row.formats,
    whitelabelEligible
  })

  return {
    ...row,
    itemPrerenderStatus: freshItem.cacheStatus,
    itemPrerenderUpdatedAt: freshItem.cacheUpdatedAt,
    agencyJobStatus: nextStatus,
    agencyJobUpdatedAt: agencyJob?.updatedAt || null,
    agencyJobLastError: agencyJob?.lastError || null,
    agencyJobKey: agencyJob?.jobKey || null,
    displayStatus
  }
}

function mergeRealtimeItems(items) {
  if (!selectedAgencyId.value || !agencyRows.value.length) {
    return
  }
  const byId = new Map(items.map((item) => [item.id, item]))
  agencyRows.value = agencyRows.value.map((row) => {
    const fresh = byId.get(row.itemId)
    if (!fresh) return row
    return applyRowFromRealtime(row, fresh)
  })
}

async function loadAgencyOptions() {
  loadingAgencies.value = true
  errorMessage.value = ''
  try {
    const agencies = await contentLibraryTemplateService.listContentLibraryCacheInspectAgencies()
    agencyOptions.value = agencies || []
    if (!selectedAgencyId.value && agencyOptions.value.length) {
      selectedAgencyId.value = agencyOptions.value[0].id
    }
  } catch (err) {
    errorMessage.value = err?.message || 'Failed to load agencies'
    handleErrors('ContentLibraryAgencyPrerenderTab.loadAgencyOptions', err)
  } finally {
    loadingAgencies.value = false
  }
}

async function loadAgencyPrerenderRows({ silent = false } = {}) {
  if (!selectedAgencyId.value) {
    agencyRows.value = []
    agencyMeta.value = null
    previousJobStatusByItem.clear()
    return
  }
  if (!silent) {
    loadingRows.value = true
  }
  errorMessage.value = ''
  try {
    const data = await contentLibraryTemplateService.inspectContentLibraryCacheByAgency(
      selectedAgencyId.value
    )
    agencyMeta.value = {
      agencyId: data.agencyId,
      name: data.name,
      whitelabelEligible: data.whitelabelEligible,
      hasContentLibraryConsumers: data.hasContentLibraryConsumers !== false,
      itemCount: data.itemCount
    }
    agencyRows.value = (data.items || []).map((row) => ({
      ...row,
      itemPrerenderStatus: row.itemCacheStatus,
      itemPrerenderUpdatedAt: row.itemCacheUpdatedAt
    }))
    syncJobStatusTracker(agencyRows.value)
  } catch (err) {
    if (!silent) {
      agencyRows.value = []
      agencyMeta.value = null
      previousJobStatusByItem.clear()
    }
    errorMessage.value = err?.message || 'Failed to load agency pre-render status'
    handleErrors('ContentLibraryAgencyPrerenderTab.loadAgencyPrerenderRows', err)
  } finally {
    if (!silent) {
      loadingRows.value = false
    }
  }
}

function startPrerenderStatusSubscription() {
  if (unsubscribePrerenderStatus) return
  unsubscribePrerenderStatus = contentLibraryService.subscribeToItems((items) => {
    mergeRealtimeItems(items)
  })
}

function stopPrerenderStatusSubscription() {
  if (unsubscribePrerenderStatus) {
    unsubscribePrerenderStatus()
    unsubscribePrerenderStatus = null
  }
  if (silentRefreshTimer) {
    clearTimeout(silentRefreshTimer)
    silentRefreshTimer = null
  }
}

async function regenerateRow(row) {
  if (!selectedAgencyId.value || regeneratingItemId.value) return
  if (agencyMeta.value?.hasContentLibraryConsumers === false) return
  regeneratingItemId.value = row.itemId
  emit('notify', 'Pre-render regeneration queued for this item and agency.', 'info')
  try {
    const response = await contentLibraryTemplateService.regenerateContentLibraryAgencyCache({
      itemId: row.itemId,
      agencyId: selectedAgencyId.value
    })
    if (response?.success && response?.status === 'queued') {
      emit('notify', 'Pre-render regeneration queued', 'success')
      const enqueuedAt = response.enqueuedAt || new Date().toISOString()
      agencyRows.value = agencyRows.value.map((entry) => {
        if (entry.itemId !== row.itemId) return entry
        previousJobStatusByItem.set(row.itemId, 'queued')
        return {
          ...entry,
          displayStatus: { key: 'processing', label: 'Processing' },
          agencyJobStatus: 'queued',
          agencyJobUpdatedAt: enqueuedAt
        }
      })
    } else {
      emit('notify', response?.error || 'Pre-render regeneration failed', 'error')
    }
  } catch (err) {
    handleErrors('ContentLibraryAgencyPrerenderTab.regenerateRow', err)
    emit('notify', 'Pre-render regeneration failed', 'error')
  } finally {
    regeneratingItemId.value = null
  }
}

watch(() => props.active, async (isActive) => {
  if (!isActive) {
    stopPrerenderStatusSubscription()
    return
  }
  if (!agencyOptions.value.length) {
    await loadAgencyOptions()
  }
  startPrerenderStatusSubscription()
  if (selectedAgencyId.value) {
    await loadAgencyPrerenderRows()
  }
}, { immediate: true })

watch(selectedAgencyId, async (agencyId) => {
  if (!props.active || !agencyId) return
  previousJobStatusByItem.clear()
  await loadAgencyPrerenderRows()
})

onUnmounted(() => {
  stopPrerenderStatusSubscription()
})
</script>

<template>
  <div class="agency-prerender-tab">
    <p class="text-muted text-14 mb-3">
      Audit pre-rendered files per agency. <strong>Regenerate</strong> uses the same action as
      <strong>Pre-render inspector</strong> (Cache inspector modal) for one item and agency.
      Status and last updated refresh live from RTDB jobs; use <strong>Refresh</strong> to
      re-check Storage file existence (formats column).
    </p>

    <div v-if="errorMessage" class="alert alert-danger py-2">{{ errorMessage }}</div>

    <div class="card mb-3">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-lg-8 col-md-7">
            <label class="form-label">Agency</label>
            <div v-if="loadingAgencies" class="text-muted small">Loading agencies…</div>
            <div v-else class="agency-prerender-agency-select">
              <BcsDropdown
                v-model="selectedAgencyId"
                :dropdown-options="agencyDropdownOptions"
                placeholder="Select an agency"
                wide
              />
            </div>
          </div>
          <div class="col-lg-4 col-md-5 d-flex gap-2 justify-content-md-end align-items-end">
            <button
              type="button"
              class="btn btn-outline-primary"
              :disabled="!selectedAgencyId || loadingRows"
              @click="loadAgencyPrerenderRows()"
            >
              <i class="bi bi-arrow-repeat me-1" />
              Refresh
            </button>
          </div>
        </div>

        <div v-if="agencyMeta" class="mt-3 d-flex flex-wrap gap-2 align-items-center">
          <span class="badge bg-light text-dark border">
            {{ agencyMeta.itemCount }} brandable item(s)
          </span>
          <span
            class="badge"
            :class="agencyMeta.whitelabelEligible ? 'bg-primary' : 'bg-secondary'"
          >
            {{ agencyMeta.whitelabelEligible ? 'White-label eligible' : 'Default only' }}
          </span>
          <span
            v-if="agencyMeta.hasContentLibraryConsumers === false"
            class="badge bg-secondary"
          >
            No Content Library access
          </span>
          <span
            v-for="(count, key) in statusSummary"
            :key="key"
            class="badge bg-light text-dark border"
          >
            {{ AGENCY_PRERENDER_DISPLAY_STATUS[key]?.label || key }}: {{ count }}
          </span>
        </div>
      </div>
    </div>

    <div class="content-library-table-shell">
      <div class="content-library-table-toolbar mb-3">
        <div class="input-group">
          <span class="input-group-text bg-white">
            <i class="bi bi-search" />
          </span>
          <input
            v-model="searchQuery"
            type="search"
            class="form-control"
            placeholder="Search by item name, document, or category"
          >
        </div>
      </div>

      <div v-if="loadingRows" class="text-center py-5 text-muted">
        <span class="spinner-border spinner-border-sm me-2" role="status" />
        Loading pre-render status for {{ selectedAgencyName || 'agency' }}…
      </div>

      <div v-else class="table-responsive">
        <table class="table table-sm align-middle content-library-admin-table agency-prerender-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Category</th>
              <th class="text-center">Agency pre-render</th>
              <th>Last updated</th>
              <th>Formats (D = default, WL = white-label)</th>
              <th class="text-center">Item pre-render (all agencies)</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in filteredRows" :key="row.itemId">
              <td>
                <div class="fw-semibold">{{ row.libraryFileName || '—' }}</div>
                <div class="text-muted small text-break">{{ row.documentFileName }}</div>
              </td>
              <td class="text-muted small">{{ row.libraryCategoryName || '—' }}</td>
              <td class="text-center">
                <span
                  class="badge d-inline-flex align-items-center"
                  :class="statusMeta(row).badgeClass"
                  :title="row.agencyJobLastError || row.inspectError || ''"
                >
                  <span
                    v-if="statusMeta(row).icon === 'spinner'"
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                  />
                  <i v-else :class="`${statusMeta(row).icon} me-1`" />
                  {{ row.displayStatus?.label || statusMeta(row).label }}
                </span>
              </td>
              <td class="small text-nowrap">
                {{ formatPrerenderTimestamp(row.agencyJobUpdatedAt) }}
              </td>
              <td class="small" :title="summarizeFormats(row.formats, agencyMeta?.whitelabelEligible)">
                {{ summarizeFormats(row.formats, agencyMeta?.whitelabelEligible) }}
              </td>
              <td class="text-center small text-muted">
                {{ row.itemPrerenderStatus || '—' }}
              </td>
              <td class="text-end">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  :disabled="Boolean(regeneratingItemId) || isRowProcessing(row) || agencyMeta.hasContentLibraryConsumers === false"
                  :title="agencyMeta.hasContentLibraryConsumers === false
                    ? 'This agency has no Content Library module or users with access'
                    : (isRowProcessing(row) ? 'Regeneration in progress' : 'Regenerate pre-render for this item and agency')"
                  @click="regenerateRow(row)"
                >
                  <span
                    v-if="regeneratingItemId === row.itemId"
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                  />
                  <i v-else class="bi bi-arrow-clockwise me-1" />
                  Regenerate
                </button>
              </td>
            </tr>
            <tr v-if="!filteredRows.length">
              <td colspan="7" class="text-center py-4 text-muted">
                {{ selectedAgencyId ? 'No brandable items match your search.' : 'Select an agency.' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.agency-prerender-tab .agency-prerender-table td {
  vertical-align: middle;
}

.agency-prerender-agency-select {
  width: 100%;
  max-width: 720px;
}
</style>
